/**
 * useFluxNodeAlertSettings Hook
 *
 * Manages FluxNode monitoring alert settings with Supabase sync.
 * Handles user preferences for which alert types to receive.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuthStore } from '../store/authStore';

export interface FluxNodeAlertSettings {
  // Node alerts
  alert_node_offline: boolean;
  alert_node_new: boolean;
  alert_rank_change: boolean;
  alert_rank_threshold: number;
  alert_tier_change: boolean;
  alert_payment_received: boolean;

  // App alerts
  alert_app_offline: boolean;
  alert_app_new: boolean;
  alert_app_expiring: boolean;
  alert_app_expire_days: number;

  // Delivery preferences
  email_enabled: boolean;
  email_digest: boolean;
  digest_frequency: 'hourly' | 'daily' | 'weekly';

  // Quiet hours
  quiet_hours_enabled: boolean;
  quiet_hours_start: string;
  quiet_hours_end: string;
}

const DEFAULT_SETTINGS: FluxNodeAlertSettings = {
  alert_node_offline: true,
  alert_node_new: true,
  alert_rank_change: true,
  alert_rank_threshold: 100,
  alert_tier_change: true,
  alert_payment_received: false,

  alert_app_offline: true,
  alert_app_new: true,
  alert_app_expiring: true,
  alert_app_expire_days: 7,

  email_enabled: true,
  email_digest: false,
  digest_frequency: 'daily',

  quiet_hours_enabled: false,
  quiet_hours_start: '22:00',
  quiet_hours_end: '08:00',
};

export function useFluxNodeAlertSettings() {
  const { isAuthenticated, user } = useAuthStore();
  const [settings, setSettings] = useState<FluxNodeAlertSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Load settings from Supabase
  const loadSettings = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const { data, error: fetchError } = await supabase
        .from('fluxnode_alert_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        // PGRST116 = no rows returned (which is fine)
        throw fetchError;
      }

      if (data) {
        setSettings({
          alert_node_offline: data.alert_node_offline ?? DEFAULT_SETTINGS.alert_node_offline,
          alert_node_new: data.alert_node_new ?? DEFAULT_SETTINGS.alert_node_new,
          alert_rank_change: data.alert_rank_change ?? DEFAULT_SETTINGS.alert_rank_change,
          alert_rank_threshold: data.alert_rank_threshold ?? DEFAULT_SETTINGS.alert_rank_threshold,
          alert_tier_change: data.alert_tier_change ?? DEFAULT_SETTINGS.alert_tier_change,
          alert_payment_received: data.alert_payment_received ?? DEFAULT_SETTINGS.alert_payment_received,
          alert_app_offline: data.alert_app_offline ?? DEFAULT_SETTINGS.alert_app_offline,
          alert_app_new: data.alert_app_new ?? DEFAULT_SETTINGS.alert_app_new,
          alert_app_expiring: data.alert_app_expiring ?? DEFAULT_SETTINGS.alert_app_expiring,
          alert_app_expire_days: data.alert_app_expire_days ?? DEFAULT_SETTINGS.alert_app_expire_days,
          email_enabled: data.email_enabled ?? DEFAULT_SETTINGS.email_enabled,
          email_digest: data.email_digest ?? DEFAULT_SETTINGS.email_digest,
          digest_frequency: data.digest_frequency ?? DEFAULT_SETTINGS.digest_frequency,
          quiet_hours_enabled: data.quiet_hours_enabled ?? DEFAULT_SETTINGS.quiet_hours_enabled,
          quiet_hours_start: data.quiet_hours_start ?? DEFAULT_SETTINGS.quiet_hours_start,
          quiet_hours_end: data.quiet_hours_end ?? DEFAULT_SETTINGS.quiet_hours_end,
        });
      }

      setError(null);
    } catch (err) {
      console.error('[FluxNode Alert Settings] Error loading:', err);
      setError(err instanceof Error ? err : new Error('Failed to load settings'));
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  // Save settings to Supabase
  const saveSettings = useCallback(async (newSettings: Partial<FluxNodeAlertSettings>) => {
    if (!isAuthenticated || !user?.id) {
      console.warn('[FluxNode Alert Settings] Cannot save - not authenticated');
      return false;
    }

    try {
      setIsSaving(true);

      const updatedSettings = { ...settings, ...newSettings };
      setSettings(updatedSettings);

      const { error: upsertError } = await supabase
        .from('fluxnode_alert_settings')
        .upsert({
          user_id: user.id,
          ...updatedSettings,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id',
        });

      if (upsertError) {
        throw upsertError;
      }

      setError(null);
      return true;
    } catch (err) {
      console.error('[FluxNode Alert Settings] Error saving:', err);
      setError(err instanceof Error ? err : new Error('Failed to save settings'));
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [isAuthenticated, user?.id, settings]);

  // Toggle a specific setting
  const toggleSetting = useCallback((key: keyof FluxNodeAlertSettings) => {
    const currentValue = settings[key];
    if (typeof currentValue === 'boolean') {
      saveSettings({ [key]: !currentValue });
    }
  }, [settings, saveSettings]);

  // Update a numeric setting
  const updateNumericSetting = useCallback((key: keyof FluxNodeAlertSettings, value: number) => {
    saveSettings({ [key]: value });
  }, [saveSettings]);

  // Update a string setting
  const updateStringSetting = useCallback((key: keyof FluxNodeAlertSettings, value: string) => {
    saveSettings({ [key]: value });
  }, [saveSettings]);

  // Reset to default settings
  const resetToDefaults = useCallback(() => {
    saveSettings(DEFAULT_SETTINGS);
  }, [saveSettings]);

  // Load settings on mount and when auth changes
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return {
    settings,
    isLoading,
    isSaving,
    error,
    isAuthenticated,
    saveSettings,
    toggleSetting,
    updateNumericSetting,
    updateStringSetting,
    resetToDefaults,
    refetch: loadSettings,
  };
}
